import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Monitor, Smartphone, Tablet, MapPin, Clock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeatureAccess, OFFICER_FEATURES } from '../../utils/rolePermissions';

import sessionService from '../../services/sessionService';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';

const SessionManagement = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  useEffect(() => {
    loadSessions();
  }, []);

  // Check officer access
  const hasAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.SESSION_MANAGEMENT);

  // Redirect non-officers
  useEffect(() => {
    if (!loading && userProfile && !hasAccess) {
      navigate('/settings-and-privacy');
    }
  }, [hasAccess, loading, userProfile, navigate]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const { data, error } = await sessionService?.getActiveSessions();
      if (!error && data) {
        setSessions(data);
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevoking(sessionId);
    try {
      const { error } = await sessionService?.revokeSession(sessionId);
      if (!error) {
        setSessions(prev => prev?.filter(s => s?.id !== sessionId));
      }
    } catch (error) {
      console.error('Failed to revoke session:', error);
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAllOthers = async () => {
    const currentSessionId = sessions?.[0]?.id;
    setRevoking('all');
    try {
      const { error } = await sessionService?.revokeAllOtherSessions(currentSessionId);
      if (!error) {
        setSessions(prev => prev?.filter(s => s?.id === currentSessionId));
      }
    } catch (error) {
      console.error('Failed to revoke sessions:', error);
    } finally {
      setRevoking(null);
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone className="w-6 h-6 text-blue-600" />;
      case 'tablet':
        return <Tablet className="w-6 h-6 text-blue-600" />;
      case 'desktop':
      default:
        return <Monitor className="w-6 h-6 text-blue-600" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date?.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Session Management - MyBodyToday</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Header with Close Button */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/settings-and-privacy')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Go back"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <Monitor className="w-6 h-6 text-indigo-600" />
                <h1 className="text-2xl font-bold text-gray-900">Session Management</h1>
              </div>
              <div className="flex items-center gap-2">
                <LogoutButton showLabel={false} ariaLabel="Sign out from session management" />
                <CloseButton
                  onClose={() => navigate('/settings-and-privacy')}
                  navigateTo="/settings-and-privacy"
                  ariaLabel="Close session management"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <strong>Security Tip:</strong> If you see a session you don't recognize, revoke it immediately
                and change your password.
              </div>
            </div>
          </div>

          {/* Sessions List */}
          <div className="space-y-4">
            {sessions?.map((session, index) => (
              <div key={session?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      {getDeviceIcon(session?.device_type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900">
                          {session?.device_name || `${session?.device_type || 'Unknown'} Device`}
                        </h3>
                        {index === 0 && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        {session?.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{session?.location}</span>
                          </div>
                        )}
                        {session?.ip_address && (
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs">{session?.ip_address}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>Last active: {formatDate(session?.last_activity_at)}</span>
                        </div>
                        {session?.user_agent && (
                          <div className="text-xs text-gray-500 mt-2">
                            {session?.user_agent}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {index !== 0 && (
                    <button
                      onClick={() => handleRevokeSession(session?.id)}
                      disabled={revoking === session?.id}
                      className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {revoking === session?.id ? 'Revoking...' : 'Revoke'}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {sessions?.length === 0 && (
              <div className="text-center py-12">
                <Monitor className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Sessions</h3>
                <p className="text-gray-600">You don't have any active sessions</p>
              </div>
            )}
          </div>
        </main>
      </div>

      <BottomTabNavigation />
    </>
  );
};

export default SessionManagement;